// The Terminal command line's parser + closed dispatch table (#4398, epic
// #4391): one pure function turning a submitted line into a `CommandIntent`.
// No DOM, no signals, no router, no i18n — the same split `filter.ts` set
// (#4397): the grammar is pure and test-first, and `CommandLine.tsx` owns
// focus, history and printing.
//
// NO-EXEC IS THE TYPE, NOT A TEST. The epic's hard constraint — a prompt that
// looks and feels like a shell and cannot execute anything anywhere, now or
// ever — is enforced by the union below: no member can name a program, a
// shell string, an HTTP verb, a URL or a body. The only effects expressible
// are in-app ones the pane layer already owns (`split`/`zoom`/`close`/
// `filter`) and in-app navigation — a `navigate` path is the SECTION path of
// a route that exists (`lib/routes.ts`), never an external URL. The test pins
// both halves (a type-level `Extract` that stops COMPILING on a write-shaped
// kind, and a runtime sweep that no parsed intent ever carries a write-shaped
// field), so the guard is a property of the table, not an absent failure. The
// table itself is a module-local literal: there is no registration API, so no
// verb can be added from outside this file.
//
// AXIS, applied not re-derived: tmux's `split-window -h` puts the new pane
// BESIDE (`row`), `-v` BELOW (`column`) — the Terminator/tmux naming trap
// `layout.ts`'s own header settles once. `-h` meaning `row` reads backwards
// until the tmux convention is the reason.
//
// OPEN resolves ONLY to routes that exist (`lib/routes.ts`), as section
// paths — no `/org/:org` prefix, the caller's router resolves the org
// segment. `open task <id>` → `devops_report` (task-addressed; `fleet_run`
// would need a task→run lookup this slice has no data for), `open plan <id>`
// → `fleet_plan`, `open run <id>` → `fleet_run`, `open box <id>` → `/fleet`
// (there is no standalone box route — the id is accepted for symmetry and
// ignored, per the issue's own "open box goes to /fleet"). `open pr <id>`
// PRINTS: no internal PR route exists at all and this union cannot open an
// external URL, and inventing either would fake a route the table cannot
// honor — the issue's step 2 instruction not to.
//
// VERB SURFACE — the issue's whole v1 set. Five act on panes (`split`,
// `zoom`, `close`, `filter`, `grep`), four drive one pane's view (`tail`,
// `follow`, `pause`, `clear`), four navigate (`open`, `workers`, `keys`,
// `vms`/`boxes`), and three answer from state the page already holds
// (`help`, `status`, `history`). This parser stays i18n-free: `help` and
// `status` are INTENTS the CommandLine seat renders through `t()`, and `print`
// carries an `en.json` KEY plus params, never a sentence minted here.
// `grep <pattern>` is `filter` without the `key=` prefix — `filter.ts`'s
// bare-text path is already a literal, case-insensitive substring matcher, so
// there is ONE matcher, not two.

/**
 * What one submitted command line means. The closed set IS the no-exec guard:
 * adding a member is the only way to add a capability, and every member is a
 * read-only, client-side effect.
 */
export type CommandIntent =
  | { readonly kind: 'split'; readonly axis: 'row' | 'column' }
  | { readonly kind: 'zoom' }
  | { readonly kind: 'close' }
  | { readonly kind: 'filter'; readonly text: string }
  | { readonly kind: 'tail'; readonly text: string }
  | { readonly kind: 'follow' }
  | { readonly kind: 'pause' }
  | { readonly kind: 'clear' }
  | { readonly kind: 'help' }
  | { readonly kind: 'status' }
  | { readonly kind: 'history' }
  | { readonly kind: 'navigate'; readonly path: NavigatePath }
  | {
      readonly kind: 'print';
      readonly key: CommandMessageKey;
      readonly params?: Readonly<Record<string, string>>;
    };

/**
 * Every message the parser can print — `en.json` keys, rendered through `t()`
 * by `CommandLine.tsx`. This file stays i18n-free by naming the KEY, never the
 * sentence; the literal union makes a typo a type error, and
 * `i18n.coverage.test.ts` proves each resolves.
 */
export type CommandMessageKey =
  | 'terminal.command_error_not_found'
  | 'terminal.command_error_split_usage'
  | 'terminal.command_error_open_usage'
  | 'terminal.command_error_open_pr'
  | 'terminal.command_error_no_args'
  | 'terminal.command_error_filter_usage'
  | 'terminal.command_error_grep_usage';

/**
 * The section paths of routes that exist in `lib/routes.ts` (fleet, fleet_run,
 * fleet_plan, devops_report, account_keys) — the ONLY shape a `navigate`
 * member can carry, so an external URL like `https://…` is not merely
 * untested but unrepresentable in the type.
 */
export type NavigatePath =
  | '/fleet'
  | '/account/keys'
  | `/fleet/tasks/${string}/report`
  | `/fleet/plans/${string}`
  | `/fleet/runs/${string}`;

const print = (key: CommandMessageKey, params?: Readonly<Record<string, string>>): CommandIntent =>
  params === undefined ? { kind: 'print', key } : { kind: 'print', key, params };
const navigate = (path: NavigatePath): CommandIntent => ({ kind: 'navigate', path });

const COMMAND_NOT_FOUND = 'terminal.command_error_not_found';
const SPLIT_USAGE = 'terminal.command_error_split_usage';
const OPEN_USAGE = 'terminal.command_error_open_usage';

function parseSplit(args: readonly string[]): CommandIntent {
  if (args.length !== 1 || (args[0] !== '-h' && args[0] !== '-v')) return print(SPLIT_USAGE);
  return { kind: 'split', axis: args[0] === '-h' ? 'row' : 'column' };
}

function parseOpen(args: readonly string[]): CommandIntent {
  const [kind, id] = args;
  if (kind === undefined || id === undefined || args.length > 2) return print(OPEN_USAGE);
  const target = Object.hasOwn(OPEN_TARGETS, kind) ? OPEN_TARGETS[kind] : undefined;
  return target === undefined ? print(OPEN_USAGE) : target(id);
}

/**
 * The `open <kind> <id>` targets, resolved against `lib/routes.ts`'s real
 * keys: `task` → `devops_report`, `plan` → `fleet_plan`, `run` →
 * `fleet_run`, `box` → `fleet` (no standalone box route — id ignored). `pr`
 * has no internal route at all, so it says so instead of inventing one.
 */
const OPEN_TARGETS: Readonly<Record<string, (id: string) => CommandIntent>> = {
  task: (id) => navigate(`/fleet/tasks/${encodeURIComponent(id)}/report`),
  pr: () => print('terminal.command_error_open_pr'),
  box: () => navigate('/fleet'),
  plan: (id) => navigate(`/fleet/plans/${encodeURIComponent(id)}`),
  run: (id) => navigate(`/fleet/runs/${encodeURIComponent(id)}`),
};

type VerbHandler = (args: readonly string[], rest: string) => CommandIntent;

/** A verb that takes no arguments: the intent, or `<verb>: takes no arguments`. */
const bare =
  (verb: string, intent: CommandIntent): VerbHandler =>
  (args) =>
    args.length === 0 ? intent : print('terminal.command_error_no_args', { verb });

/**
 * The whole dispatch surface, a closed literal. Handlers get the split tokens
 * and the RAW remainder after the verb — `filter`/`tail`/`grep` need the raw
 * form so quoted values survive verbatim into `parseTerminalFilter` at the
 * caller. `workers`, `vms` and `boxes` all land on `/fleet`: the fleet page is
 * the workers view, and there is no standalone box route (same as `open box`).
 */
const VERBS: Readonly<Record<string, VerbHandler>> = {
  split: (args) => parseSplit(args),
  zoom: bare('zoom', { kind: 'zoom' }),
  close: bare('close', { kind: 'close' }),
  filter: (_args, rest) =>
    rest === '' ? print('terminal.command_error_filter_usage') : { kind: 'filter', text: rest },
  grep: (_args, rest) =>
    rest === '' ? print('terminal.command_error_grep_usage') : { kind: 'filter', text: rest },
  tail: (_args, rest) => ({ kind: 'tail', text: rest }),
  follow: bare('follow', { kind: 'follow' }),
  pause: bare('pause', { kind: 'pause' }),
  clear: bare('clear', { kind: 'clear' }),
  help: bare('help', { kind: 'help' }),
  status: bare('status', { kind: 'status' }),
  history: bare('history', { kind: 'history' }),
  open: (args) => parseOpen(args),
  workers: bare('workers', navigate('/fleet')),
  vms: bare('vms', navigate('/fleet')),
  boxes: bare('boxes', navigate('/fleet')),
  keys: bare('keys', navigate('/account/keys')),
};

/** Every verb the table answers, sorted — what `help` lists and Tab completes over. */
export const COMMAND_VERBS: readonly string[] = Object.keys(VERBS).sort();

/** Parse one submitted line into its intent. Total: every string has an answer. */
export function parseCommand(line: string): CommandIntent {
  const trimmed = line.trim();
  if (trimmed === '') return print(COMMAND_NOT_FOUND);
  const [verb = '', ...args] = trimmed.split(/\s+/);
  const handler = Object.hasOwn(VERBS, verb) ? VERBS[verb] : undefined;
  if (handler === undefined) return print(COMMAND_NOT_FOUND);
  return handler(args, trimmed.slice(verb.length).trim());
}
